import React from "react";
import { Instagram, Play } from "lucide-react";
import { videos, INSTAGRAM_PROFILE_URL, INSTAGRAM_HANDLE } from "../data/videos";

export const Videos = () => {
  return (
    <section id="videos" data-testid="videos-section" className="py-24 md:py-32 bg-[#0E1A2B]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#C9A869]" />
            <span className="text-[11px] tracking-[0.4em] uppercase text-[#C9A869]">Vidéos</span>
            <span className="h-px w-10 bg-[#C9A869]" />
          </div>
          <h2 className="serif font-light text-4xl md:text-5xl lg:text-6xl text-[#F7F3EA] tracking-tight">
            Voir <span className="italic gold-text">l'énergie</span> des cours.
          </h2>
          <p className="mt-6 text-sm md:text-base text-[#F7F3EA]/70 max-w-2xl mx-auto font-light leading-relaxed">
            Quelques exemples en vidéo pour découvrir le style, l'énergie et la pédagogie.
            Retrouvez les nouveaux reels sur Instagram&nbsp;
            <a
              href={INSTAGRAM_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="videos-instagram-handle"
              className="text-[#C9A869] hover:text-[#DCC28E] underline underline-offset-4 transition-colors"
            >
              {INSTAGRAM_HANDLE}
            </a>.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 justify-items-center">
          {videos.map((v) => (
            <VideoCard key={v.id} video={v} />
          ))}
        </div>

        <div className="mt-14 flex justify-center">
          <a
            href={INSTAGRAM_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="videos-instagram-cta"
            className="inline-flex items-center gap-3 px-8 py-4 border border-[#C9A869] text-[#C9A869] text-[11px] tracking-[0.25em] uppercase hover:bg-[#C9A869] hover:text-[#0E1A2B] transition-colors duration-500"
          >
            <Instagram size={16} />
            Suivre sur Instagram
          </a>
        </div>
      </div>
    </section>
  );
};

const VideoCard = ({ video }) => {
  const hasReel = Boolean(video.instagramUrl);
  const href = hasReel ? video.instagramUrl : INSTAGRAM_PROFILE_URL;
  const subtitle = hasReel ? video.subtitle : "Bientôt en ligne";

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`video-card-${video.id}`}
      aria-label={hasReel ? `Voir ${video.title} sur Instagram` : `Suivre ${INSTAGRAM_HANDLE} sur Instagram`}
      className="group block w-full max-w-[320px]"
    >
      {/* Decorative outer frame */}
      <div className="relative">
        <div className="absolute -inset-2 border border-[#C9A869]/25 group-hover:border-[#C9A869]/60 transition-colors duration-500 pointer-events-none" />

        {/* The 9:16 vertical card */}
        <div className="relative aspect-[9/16] overflow-hidden bg-[#1A2A40] border border-[#C9A869] shadow-[0_15px_40px_rgba(0,0,0,0.45)]">
          {/* Poster */}
          <img
            src={video.poster}
            alt={video.title}
            loading="lazy"
            className={`absolute inset-0 w-full h-full object-cover ${video.posterPosition || "object-center"} transition-transform duration-[1500ms] ease-out group-hover:scale-[1.06] ${hasReel ? "" : "opacity-55"}`}
          />

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0E1A2B] via-[#0E1A2B]/30 to-transparent" />
          <div className="absolute inset-0 bg-[#0E1A2B]/15 group-hover:bg-[#0E1A2B]/0 transition-colors duration-700" />

          {/* Top-right Instagram tag */}
          <div className="absolute top-4 right-4 inline-flex items-center gap-2 px-3 py-1.5 bg-[#0E1A2B]/70 backdrop-blur-sm border border-[#C9A869]/50">
            <Instagram size={12} className="text-[#C9A869]" />
            <span className="text-[9px] tracking-[0.25em] uppercase text-[#F7F3EA]/90">
              {hasReel ? "Reel" : "Profil"}
            </span>
          </div>

          {/* Center play button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <span className="absolute inset-0 rounded-full border border-[#C9A869]/60 scale-125 group-hover:scale-150 group-hover:border-[#C9A869] transition-all duration-700" />
              <span className="absolute inset-0 rounded-full border border-[#C9A869]/30 scale-150 group-hover:scale-[1.85] transition-all duration-1000" />
              <div className="relative w-20 h-20 rounded-full bg-[#C9A869] text-[#0E1A2B] flex items-center justify-center shadow-[0_10px_40px_rgba(201,168,105,0.35)] group-hover:bg-[#DCC28E] group-hover:scale-105 transition-all duration-500">
                {hasReel ? (
                  <Play size={26} fill="#0E1A2B" className="ml-1" />
                ) : (
                  <Instagram size={24} />
                )}
              </div>
            </div>
          </div>

          {/* Bottom title overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <p className="serif text-2xl text-[#F7F3EA] leading-tight">{video.title}</p>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#C9A869]/90 mt-1.5">
              {subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Footer label below card */}
      <div className="mt-5 flex items-center justify-between">
        <span className="text-[10px] tracking-[0.3em] uppercase text-[#F7F3EA]/55">
          {INSTAGRAM_HANDLE}
        </span>
        <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#C9A869] group-hover:text-[#DCC28E] transition-colors">
          {hasReel ? "Lire le reel" : "Voir le profil"}
          <span aria-hidden className="transition-transform duration-500 group-hover:translate-x-1">→</span>
        </span>
      </div>
    </a>
  );
};
